import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Search, Users } from 'lucide-react';
import StatusPill from '../components/StatusPill';

export default function Customers() {
    const [tab, setTab] = useState('all');
    const [items, setItems] = useState([]);
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (tab !== 'all') params.type = tab;
            if (q) params.q = q;
            const r = await api.get('/customers', { params });
            setItems(r.data.items || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [tab]);

    return (
        <AppShell pageTitle="Customers">
            <Card className="p-3 sm:p-4 mb-4 flex items-center gap-3">
                <div className="relative flex-1 max-w-md">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') load(); }} placeholder="Search name, mobile, email" className="pl-9" data-testid="customer-search-input" />
                </div>
                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList>
                        <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
                        <TabsTrigger value="retail" data-testid="tab-retail">Retail</TabsTrigger>
                        <TabsTrigger value="wholesale" data-testid="tab-wholesale">Wholesale</TabsTrigger>
                    </TabsList>
                </Tabs>
            </Card>

            {loading ? <LoadingBlock /> : items.length === 0 ? (
                <EmptyState icon={Users} title="No customers" description="Customers will appear here once they register." />
            ) : (
                <Card className="p-0 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="customers-table">
                        <thead>
                            <tr className="border-b bg-neutral-50 text-xs text-neutral-500">
                                <th className="text-left p-3 font-medium">Name</th>
                                <th className="text-left font-medium">Mobile</th>
                                <th className="text-left font-medium">Type</th>
                                <th className="text-left font-medium">Level</th>
                                <th className="text-left font-medium">Location</th>
                                <th className="text-left font-medium">Business</th>
                                <th className="text-left font-medium">Status</th>
                                <th className="text-left p-3 font-medium">Joined</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(c => (
                                <tr key={c.id} className="border-b hover:bg-neutral-50" data-testid={`customer-row-${c.mobile}`}>
                                    <td className="p-3"><div className="font-medium">{c.name}</div><div className="text-xs text-neutral-500">{c.email || '—'}</div></td>
                                    <td className="font-mono text-xs">{c.mobile}</td>
                                    <td className="capitalize">{c.type}</td>
                                    <td>{c.level ? `L${c.level}` : '—'}</td>
                                    <td className="text-xs">{c.location?.state || '—'} {c.location?.pincode ? `· ${c.location.pincode}` : ''}</td>
                                    <td className="text-xs">{c.business_name || '—'}</td>
                                    <td><StatusPill status={c.status || 'active'} /></td>
                                    <td className="p-3 text-xs text-neutral-600">{new Date(c.created_at).toLocaleDateString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}
        </AppShell>
    );
}
